import React from "react";
import { Dialog, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import { HubButton, HubIcon } from "../../components";
import { usePostCreatePart } from "../../hooks";
import { QUOTE_STAGE_LABELS } from "../../constants/salesStages";
import { useCostingSheetUpload } from "../../pages/QuoteBuilderPage/components/useCostingSheetUpload";

const partsPerTreeTemplate = (partsPerTree) =>
  Number(partsPerTree) > 1 ? 1 : 2;

const QuoteStageActionsDialog = ({ quote, stage, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stageInfo = QUOTE_STAGE_LABELS.find((s) => s.id === stage);
  const { upload, working: uploading } = useCostingSheetUpload({
    quoteId: quote?.id,
  });
  const { mutate: createPart, isLoading: creating } = usePostCreatePart();

  if (!stageInfo || !quote) return null;

  const hasPart = !!(quote.partId ?? quote.part?.id);

  const showUpload = !!stageInfo.promptUpload;
  const showCreate = !!stageInfo.promptCreate && !hasPart;

  if (!showUpload && !showCreate) return null;

  const onCostingSheet = document.getElementById("page-1");

  const handleUpload = () => {
    if (onCostingSheet) {
      upload();
      onClose();
      return;
    }
    if (quote.quoteNumber) {
      navigate(`/quotes/${quote.quoteNumber}?upload=1`);
      onClose();
    }
  };

  const handleCreatePart = () => {
    createPart(
      {
        customerId: quote.customer?.id || quote.customerId,
        partName: quote.partName || quote.part?.partName,
        partNumber: quote.partNumber || quote.part?.partNumber,
        weight: Number(quote.weight) || 0,
        templateId: partsPerTreeTemplate(quote.partsPerTree),
        quoteId: quote.id,
        lastSentPrice: Number(quote.total) || 0,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["/sales/quotes"]);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <Stack gap="18px" padding="50px" alignItems="center">
        <Stack
          backgroundColor="hubColors.greyLight"
          borderRadius="50%"
          padding="20px"
          margin="10px"
        >
          <HubIcon icon="FileText" size="24px" />
        </Stack>
        <Stack alignItems="center" gap="4px">
          <Typography variant="h6">Quote moved to {stageInfo.label}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {showUpload && showCreate
              ? "Would you like to upload the costing sheet to Google Drive and create a part from this quote?"
              : showUpload
                ? "Would you like to upload the costing sheet to Google Drive?"
                : "No part exists for this quote yet — would you like to create one?"}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          gap="10px"
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <HubButton
            variant="contained"
            color="grey"
            label="Skip"
            onClick={onClose}
            disabled={uploading || creating}
            size="xsmall"
          />
          {showUpload && (
            <HubButton
              variant="contained"
              icon="UploadCloud"
              iconProps={{ size: 14 }}
              size="xsmall"
              label={uploading ? "Uploading…" : "Upload to Google Drive"}
              onClick={handleUpload}
              disabled={uploading}
            />
          )}
          {showCreate && (
            <HubButton
              variant="contained"
              icon="Plus"
              iconProps={{ size: 14 }}
              label={creating ? "Creating…" : "Create Part from Quote"}
              onClick={handleCreatePart}
              disabled={creating}
              size="xsmall"
            />
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default QuoteStageActionsDialog;
